import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Image,
  Dimensions,
} from 'react-native';
import LinearGradient from './LinearGradient';
import Icon from 'react-native-vector-icons/Ionicons';
import type { ButtonProps, ProfileCardProps, StatusBadgeProps } from '../types';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const CARD_IMAGE_HEIGHT = SCREEN_WIDTH * 1.1;

export const THEME = {
  primary: '#C2185B',
  primaryDark: '#880E4F',
  secondary: '#F39C12',
  bg: '#f8f4f6',
  card: '#FFFFFF',
  border: '#E8DDD5',
  text: '#2C1810',
  textLight: '#8B6355',
  success: '#27AE60',
  danger: '#E74C3C',
  warning: '#F39C12',
} as const;

// ─── Button ──────────────────────────────────────────────────────────────────

export const Button: React.FC<ButtonProps> = ({
  title,
  onPress,
  style,
  textStyle,
  loading = false,
  variant = 'primary',
  disabled = false,
}) => {
  const bg =
    variant === 'danger' ? THEME.danger
    : variant === 'success' ? THEME.success
    : variant === 'outline' ? 'transparent'
    : THEME.primary;

  return (
    <TouchableOpacity
      style={[
        styles.btn,
        { backgroundColor: bg, borderColor: bg === 'transparent' ? THEME.primary : bg },
        variant === 'outline' && { borderWidth: 2 },
        (disabled || loading) && { opacity: 0.5 },
        style,
      ]}
      onPress={onPress}
      disabled={disabled || loading}
      activeOpacity={0.8}>
      {loading ? (
        <ActivityIndicator color={variant === 'outline' ? THEME.primary : '#fff'} />
      ) : (
        <Text style={[styles.btnText, variant === 'outline' && { color: THEME.primary }, textStyle]}>
          {title}
        </Text>
      )}
    </TouchableOpacity>
  );
};

// ─── Profile Card ─────────────────────────────────────────────────────────────

const calcAge = (dob?: string): number | null => {
  if (!dob) return null;
  let date: Date;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) {
    // DD/MM/YYYY
    const [dd, mm, yyyy] = dob.split('/').map(Number);
    date = new Date(yyyy, mm - 1, dd);
  } else {
    // ISO YYYY-MM-DD fallback
    date = new Date(dob);
  }
  if (isNaN(date.getTime())) return null;
  return Math.floor((Date.now() - date.getTime()) / (365.25 * 24 * 3600 * 1000));
};

export const ProfileCard: React.FC<ProfileCardProps> = ({ profile, onPress }) => {
  const age = calcAge(profile.dob);
  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);
  // Use thumbnail for card list (fast), fall back to full photo
  const imageUri = profile.thumbnailUrl ?? profile.photoUrl;
  const blurred = !!profile.photoPrivacy && profile.connectedWithViewer === false;

  return (
    <TouchableOpacity style={styles.card} onPress={onPress} activeOpacity={0.85}>
      {/* Placeholder shown while loading or on error */}
      {(imgLoading || imgError || !imageUri) && (
        <View style={styles.cardImagePlaceholder}>
          {imgLoading && imageUri && !imgError
            ? <ActivityIndicator color={THEME.primary} size="large" />
            : <View style={styles.cardImageNoPhoto}>
                <Text style={styles.cardImageNoPhotoText}>
                  {profile.name?.charAt(0)?.toUpperCase() ?? '?'}
                </Text>
              </View>
          }
        </View>
      )}
      {!!imageUri && !imgError && (
        <Image
          style={[styles.cardImage, imgLoading && { position: 'absolute', opacity: 0 }]}
          source={{ uri: imageUri }}
          resizeMode="cover"
          blurRadius={blurred ? 18 : 0}
          onLoad={() => setImgLoading(false)}
          onError={() => { setImgLoading(false); setImgError(true); }}
        />
      )}
      {blurred && (
        <View style={styles.cardBlurOverlay}>
          <Icon name="lock-closed" size={24} color="#fff" />
          <Text style={styles.cardBlurText}>Accept interest{'\n'}to view photo</Text>
        </View>
      )}
      {profile.featured && (
        <View style={styles.featuredBadge}>
          <Text style={styles.featuredBadgeText}>★ Featured</Text>
        </View>
      )}

      {/* ── Top-left badge row: Kundali · LinkedIn ── */}
      {(!!profile.nakshatra || profile.linkedinVerified) && (
        <View style={styles.cardTopBadges}>
          {!!profile.nakshatra && (
            <View style={styles.cardTopPill}>
              <Text style={styles.cardTopPillText}>★ Kundali</Text>
            </View>
          )}
          {profile.linkedinVerified && (
            <View style={[styles.cardTopPill, styles.cardTopPillLinkedin]}>
              <Icon name="logo-linkedin" size={9} color="#fff" />
              <Text style={styles.cardTopPillText}> In</Text>
            </View>
          )}
        </View>
      )}

      <View style={styles.cardOverlay}>
        <LinearGradient
          colors={['transparent', 'rgba(0,0,0,0.25)', 'rgba(0,0,0,0.72)']}
          locations={[0, 0.4, 1]}
          style={StyleSheet.absoluteFill}
        />
        <View style={styles.cardNameRow}>
          <Text style={styles.cardName}>{profile.name}</Text>
          <View style={styles.cardBadgeRow}>
            {age !== null && (
              <View style={styles.cardAgePill}>
                <Text style={styles.cardAgePillText}>{age} yrs</Text>
              </View>
            )}
            {!!profile.personHeight && (
              <View style={styles.cardHeightPill}>
                <Text style={styles.cardHeightPillText}>{profile.personHeight}</Text>
              </View>
            )}
          </View>
        </View>
        {!!profile.profileCode && (
          <Text style={styles.cardProfileCode}>{profile.profileCode}</Text>
        )}
        {!!(profile.casteSubType1 || profile.surname || profile.gotra) && (
          <Text style={styles.cardSub}>
            {[profile.casteSubType1, profile.surname, profile.gotra].filter(Boolean).join(' • ')}
          </Text>
        )}
        {!!(profile.highestEducation || profile.job) && (
          <Text style={styles.cardSub}>
            {[profile.highestEducation, profile.job].filter(Boolean).join(' • ')}
          </Text>
        )}
        {profile.district && (
          <Text style={styles.cardLocation}>
            📍 {profile.district}
          </Text>
        )}
      </View>
    </TouchableOpacity>
  );
};

// ─── Status Badge ─────────────────────────────────────────────────────────────

type StatusConfig = { bg: string; text: string; label: string };
const STATUS_MAP: Record<string, StatusConfig> = {
  DRAFT:            { bg: '#EBF5FB', text: '#1A5276', label: '✏️ Draft' },
  PENDING_APPROVAL: { bg: '#FEF9E7', text: '#B7950B', label: '⏳ Pending Review' },
  ACTIVE:           { bg: '#EAFAF1', text: '#1E8449', label: '✅ Active' },
  REJECTED:         { bg: '#FDEDEC', text: '#922B21', label: '❌ Rejected' },
  INACTIVE:         { bg: '#F2F3F4', text: '#626567', label: '⏸ Inactive' },
  PENDING_DELETION: { bg: '#FDEDEC', text: '#922B21', label: '🗑 Deletion Pending' },
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const s = STATUS_MAP[status] ?? STATUS_MAP.DRAFT;
  return (
    <View style={[styles.statusBadge, { backgroundColor: s.bg }]}>
      <Text style={[styles.statusText, { color: s.text }]}>{s.label}</Text>
    </View>
  );
};

// ─── Section Header ───────────────────────────────────────────────────────────

interface SectionHeaderProps {
  title: string;
  action?: string;
  onAction?: () => void;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({ title, action, onAction }) => (
  <View style={styles.sectionHeader}>
    <Text style={styles.sectionTitle}>{title}</Text>
    {action && (
      <TouchableOpacity onPress={onAction}>
        <Text style={styles.sectionAction}>{action}</Text>
      </TouchableOpacity>
    )}
  </View>
);

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  btn: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnText: { color: '#fff', fontWeight: '700', fontSize: 17 },
  card: {
    backgroundColor: THEME.card,
    borderRadius: 20,
    marginHorizontal: 16,
    marginVertical: 8,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.09,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 3 },
    elevation: 4,
  },
  cardImage: { width: '100%', height: CARD_IMAGE_HEIGHT, backgroundColor: '#EDE0D8' },
  cardImagePlaceholder: {
    width: '100%', height: CARD_IMAGE_HEIGHT,
    backgroundColor: '#EDE0D8', alignItems: 'center', justifyContent: 'center',
  },
  cardImageNoPhoto: {
    width: 80, height: 80, borderRadius: 40,
    backgroundColor: THEME.primary, alignItems: 'center', justifyContent: 'center',
  },
  cardImageNoPhotoText: { fontSize: 36, fontWeight: '900', color: '#fff' },
  cardBlurOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    alignItems: 'center', justifyContent: 'center', gap: 8,
  },
  cardBlurText: { fontSize: 12, fontWeight: '700', color: '#fff', textAlign: 'center', lineHeight: 17 },
  cardOverlay: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    paddingHorizontal: 16, paddingTop: 60, paddingBottom: 16,
  },
  cardNameRow: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 6 },
  cardName: { fontSize: 26, fontWeight: '900', color: '#fff', flex: 1, letterSpacing: 0.3 },
  cardBadgeRow: { flexDirection: 'row', gap: 6, marginLeft: 8, flexShrink: 0 },
  cardAgePill: { backgroundColor: 'rgba(255,255,255,0.18)', borderRadius: 20, paddingHorizontal: 10, paddingVertical: 4, borderWidth: 1, borderColor: 'rgba(255,255,255,0.35)' },
  cardAgePillText: { fontSize: 14, fontWeight: '700', color: '#ffcce0' },
  cardHeightPill: { backgroundColor: 'rgba(255,255,255,0.22)', borderRadius: 20, paddingHorizontal: 10, paddingVertical: 4, borderWidth: 1, borderColor: 'rgba(255,255,255,0.4)' },
  cardHeightPillText: { fontSize: 14, fontWeight: '700', color: '#fff' },
  cardProfileCode: { fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 2, fontWeight: '600', letterSpacing: 0.5 },
  cardSub: { fontSize: 15, color: 'rgba(255,255,255,0.9)', marginTop: 4, fontWeight: '500' },
  cardLocation: { fontSize: 14, color: 'rgba(255,255,255,0.75)', marginTop: 6 },
  featuredBadge: {
    position: 'absolute', top: 14, right: 14, zIndex: 10,
    backgroundColor: '#F39C12', borderRadius: 20,
    paddingHorizontal: 10, paddingVertical: 4,
    flexDirection: 'row', alignItems: 'center',
  },
  featuredBadgeText: { color: '#fff', fontSize: 12, fontWeight: '800' },
  badge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 20,
    marginTop: 6,
    backgroundColor: '#EDE0D8',
  },
  badgeText: { fontSize: 14, fontWeight: '600', color: THEME.textLight },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 20,
    alignSelf: 'flex-start',
  },
  statusText: { fontSize: 13, fontWeight: '700' },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    marginTop: 20,
    marginBottom: 8,
  },
  sectionTitle: { fontSize: 20, fontWeight: '800', color: THEME.text },
  sectionAction: { fontSize: 16, color: THEME.primary, fontWeight: '600' },
  cardTopBadges: {
    position: 'absolute', top: 12, left: 12, zIndex: 10,
    flexDirection: 'row', gap: 5,
  },
  cardTopPill: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: 'rgba(194,24,91,0.55)',
    borderRadius: 20, paddingHorizontal: 7, paddingVertical: 3,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.3)',
  },
  cardTopPillLinkedin: { backgroundColor: 'rgba(10,102,194,0.55)' },
  cardTopPillText: { color: '#fff', fontSize: 10, fontWeight: '700' },
});
